/**
 * Providers, in the order everything else depends on them.
 */
import { Suspense, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { ThemeProvider } from 'next-themes'
import { Toaster } from '@/components/ui/sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError, setUnauthorizedHandler } from '@/lib/api'
import { router } from '@/routes'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Per-hook stale times live in `queries.ts`; this is the floor.
      staleTime: 60_000,
      // Balances are refreshed by the backend on a schedule, so remounting a
      // component or tabbing back to the window is not new information.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // A 4xx will fail identically however many times we ask.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      },
    },
    mutations: { retry: false },
  },
})

export function App() {
  // Session expiry becomes a client-side navigation instead of a page reload,
  // which keeps the cache and the scroll position intact.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (window.location.pathname === '/login') return
      const next = `${window.location.pathname}${window.location.search}`
      void router.navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true })
    })
  }, [])

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<BootSkeleton />}>
          <RouterProvider router={router} />
        </Suspense>
        <Toaster position="bottom-right" />
      </QueryClientProvider>
    </ThemeProvider>
  )
}

function BootSkeleton() {
  return (
    <div className="bg-ink min-h-dvh p-6">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="mt-6 h-64 w-full" />
    </div>
  )
}

export default App
